import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, Sparkles, CheckCircle2, Lock, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Dashboard-side Peak Graduate progress tracker.
 *
 * Renders a 3-pillar summary (Foundations · Indices · Advanced) where each
 * pillar is one of:
 *   - "empty"   → no cert yet, needs quizzes + lessons
 *   - "basic"   → holds a Basic-variant cert; one CTA away from Mastery
 *   - "mastery" → cert is Mastery, pillar unlocked
 *
 * Hidden entirely when:
 *   - user isn't signed in
 *   - user has already been issued the Peak Graduate diploma
 *   - the API errors out (fail-quiet — the dashboard has plenty of signal
 *     already; a spinner here would create noise)
 */
export default function SummitGraduateProgress({ user }) {
  const { t, i18n } = useTranslation();
  const [master, setMaster] = useState(null);
  const [alreadyIssued, setAlreadyIssued] = useState(false);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let cancelled = false;
    api.get(`/certificates/master/status?language=${i18n.language || "en"}`)
      .then((r) => {
        if (cancelled) return;
        setMaster(r.data);
        setAlreadyIssued(Boolean(r.data.certificate));
      })
      .catch(() => { /* silent — dashboard survives without this widget */ });
    return () => { cancelled = true; };
  }, [user, i18n.language]);

  if (!user || typeof user !== "object") return null;
  if (!master) return null;
  if (alreadyIssued) return null; // diploma earned — no more nudging

  const pillars = [
    ...(master.completed || []).map((c) => ({ ...c, state: "mastery" })),
    ...(master.missing || []).map((c) => ({
      ...c,
      state: c.action === "upgrade" ? "basic" : "empty",
    })),
  ];

  // Sort pillars into the canonical order Foundations → Indices → Advanced
  const ORDER = { "foundations": 0, "indices": 1, "commodities-forex-cfds": 2 };
  pillars.sort((a, b) => (ORDER[a.slug] ?? 9) - (ORDER[b.slug] ?? 9));

  const masteryCount = pillars.filter((p) => p.state === "mastery").length;
  const total = master.required_count || 3;

  return (
    <div
      data-testid="dashboard-summit-graduate-tracker"
      className="relative overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-slate-950 via-slate-950 to-blue-950 text-white p-6 sm:p-7"
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 font-semibold mb-2">
              <Trophy size={12} /> {t("dashboard.grad.eyebrow", "Peak Graduate progress")}
            </div>
            <h3 className="font-display font-bold text-xl text-white leading-tight tracking-tight">
              {t("dashboard.grad.title", "Three Mastery certs unlock your diploma.")}
            </h3>
            <p className="mt-1 text-xs text-slate-400">
              {t("dashboard.grad.subtitle", {
                done: masteryCount,
                total,
                count: total,
                defaultValue: "{{done}} of {{total}} pillars mastered.",
              })}
            </p>
          </div>
          <Link
            to="/certificates"
            data-testid="dashboard-summit-graduate-view"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-300 hover:text-amber-200 transition-colors"
          >
            {t("dashboard.grad.viewAll", "See details")} <ArrowRight size={12} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {pillars.map((p) => (
            <Pillar key={p.slug} pillar={p} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Pillar({ pillar, t }) {
  const isMastery = pillar.state === "mastery";
  const isBasic = pillar.state === "basic";

  return (
    <Link
      to={pillar.course_id ? `/courses/${pillar.course_id}` : "/courses"}
      data-testid={`grad-pillar-${pillar.slug}`}
      className={`group relative rounded-xl border p-4 flex flex-col gap-2 transition-all ${
        isMastery
          ? "bg-amber-500/10 border-amber-500/40 hover:bg-amber-500/15"
          : isBasic
          ? "bg-white/5 border-amber-400/50 hover:bg-amber-500/8"
          : "bg-white/5 border-white/10 hover:bg-white/8"
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex w-6 h-6 rounded-md items-center justify-center shrink-0 ${
            isMastery
              ? "bg-amber-500 text-slate-950"
              : isBasic
              ? "bg-amber-400/25 text-amber-300"
              : "bg-white/10 text-slate-400"
          }`}
        >
          {isMastery ? <CheckCircle2 size={13} /> : isBasic ? <Sparkles size={12} /> : <Lock size={11} />}
        </span>
        <span
          className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
            isMastery ? "text-amber-300" : isBasic ? "text-amber-400" : "text-slate-500"
          }`}
        >
          {isMastery
            ? t("dashboard.grad.state.mastery", "Mastery")
            : isBasic
            ? t("dashboard.grad.state.basic", "Basic — upgradable")
            : t("dashboard.grad.state.empty", "Not started")}
        </span>
      </div>
      <div className={`font-display font-semibold text-sm leading-tight ${
        isMastery ? "text-white" : "text-slate-200"
      }`}>
        {pillar.course_title}
      </div>
      <div className={`text-[11px] font-semibold ${
        isMastery
          ? "text-emerald-300"
          : isBasic
          ? "text-amber-300 group-hover:text-amber-200"
          : "text-slate-500 group-hover:text-slate-400"
      }`}>
        {isMastery
          ? t("dashboard.grad.action.done", "Cert earned")
          : isBasic
          ? t("dashboard.grad.action.upgrade", "Finish premium → auto-upgrade →")
          : t("dashboard.grad.action.start", "Start the course →")}
      </div>
    </Link>
  );
}
